<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainAssistant from 'dashboard/api/captain/assistant';

import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { buildTemplates, buildBlankTemplate } from './createFlowTemplates.js';

const props = defineProps({
  // Guidelines/guardrails/scenarios are Captain V2 concepts; hide the chips
  // that promise them when the account can't use them.
  showBehavior: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['select', 'drafted']);

const { t, tm, rt } = useI18n();

// A one-line brief can't produce a meaningful draft, so ask for a bit more.
const MIN_BRIEF_LENGTH = 40;

const brief = ref('');
const briefError = ref('');
const isDrafting = ref(false);

const templates = computed(() => [
  ...buildTemplates({ t, tm, rt }),
  buildBlankTemplate({ t }),
]);

const templateIncludes = template => {
  if (!props.showBehavior) return [];

  return [
    template.guidelines.length && {
      key: 'guidelines',
      label: t(
        'CAPTAIN.ASSISTANTS.CREATE_FLOW.START.INCLUDES_GUIDELINES',
        template.guidelines.length
      ),
    },
    template.guardrails.length && {
      key: 'guardrails',
      label: t(
        'CAPTAIN.ASSISTANTS.CREATE_FLOW.START.INCLUDES_GUARDRAILS',
        template.guardrails.length
      ),
    },
    template.scenarios.length && {
      key: 'scenarios',
      label: t(
        'CAPTAIN.ASSISTANTS.CREATE_FLOW.START.INCLUDES_SCENARIOS',
        template.scenarios.length
      ),
    },
  ].filter(Boolean);
};

const normalizeDraft = draft => ({
  name: draft.name || '',
  description: draft.description || '',
  productName: draft.product_name || '',
  guidelines: (draft.response_guidelines || []).filter(Boolean),
  guardrails: (draft.guardrails || []).filter(Boolean),
  scenarios: (draft.scenarios || []).filter(
    scenario =>
      scenario?.title && scenario?.description && scenario?.instruction
  ),
});

const draftAgent = async () => {
  if (brief.value.trim().length < MIN_BRIEF_LENGTH) {
    briefError.value = t(
      'CAPTAIN.ASSISTANTS.CREATE_FLOW.START.DESCRIBE_MIN_LENGTH'
    );
    return;
  }

  briefError.value = '';
  isDrafting.value = true;
  try {
    const { data } = await CaptainAssistant.draft({ brief: brief.value });
    emit('drafted', normalizeDraft(data.draft || {}));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('CAPTAIN.ASSISTANTS.CREATE_FLOW.START.DESCRIBE_ERROR');
    useAlert(errorMessage);
  } finally {
    isDrafting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.START.TEMPLATE_TITLE') }}
        </h3>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.START.TEMPLATE_SUBTITLE') }}
        </p>
      </div>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2" role="list">
        <button
          v-for="template in templates"
          :key="template.key"
          type="button"
          role="listitem"
          class="flex flex-col gap-3 p-5 text-left transition-shadow rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container hover:shadow-md focus-visible:outline-2 focus-visible:outline-n-brand"
          @click="emit('select', template)"
        >
          <div class="flex items-center gap-3">
            <span
              class="grid rounded-lg size-9 place-items-center bg-n-violet-9/10 text-n-violet-11"
            >
              <span :class="template.icon" class="size-5" />
            </span>
            <span class="text-sm font-semibold text-n-slate-12">
              {{ template.title }}
            </span>
          </div>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ template.description }}
          </p>
          <div
            v-if="templateIncludes(template).length"
            class="flex flex-wrap gap-1.5"
          >
            <span
              v-for="chip in templateIncludes(template)"
              :key="chip.key"
              class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              {{ chip.label }}
            </span>
          </div>
        </button>
      </div>
    </div>

    <div
      class="flex flex-col gap-3 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <div class="flex items-center gap-3">
        <span
          class="grid rounded-lg size-9 place-items-center bg-n-violet-9/10 text-n-violet-11"
        >
          <span class="i-lucide-sparkles size-5" />
        </span>
        <div>
          <h3 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.START.DESCRIBE_TITLE') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.START.DESCRIBE_SUBTITLE') }}
          </p>
        </div>
      </div>
      <TextArea
        v-model="brief"
        auto-height
        :max-length="2000"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.CREATE_FLOW.START.DESCRIBE_PLACEHOLDER')
        "
        :message="briefError"
        :message-type="briefError ? 'error' : 'info'"
        min-height="6rem"
      />
      <div class="flex justify-end">
        <Button
          type="button"
          icon="i-lucide-sparkles"
          :label="t('CAPTAIN.ASSISTANTS.CREATE_FLOW.START.DESCRIBE_BUTTON')"
          :is-loading="isDrafting"
          :disabled="isDrafting"
          @click="draftAgent"
        />
      </div>
    </div>
  </div>
</template>
